import { ArrowsAltOutlined, CloseOutlined } from '@ant-design/icons';
import { Alert, Modal as AntModal, Button, Flex } from 'antd';
import { ChevronSteps } from 'components/ChevronSteps';
import { WhiteModal } from 'components/Modal';
import { clampStep } from 'components/multiStepModal/stepUtils';
import { MultiStepModalProps } from 'components/multiStepModal/types';
import useWizardPrimaryAction from 'components/multiStepModal/useWizardPrimaryAction';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

export type {
  FormModalProps,
  MultiStepModalProps,
  WizardLabels,
  WizardStep,
  WizardSubmit,
  WizardSuccessRenderArgs,
  WizardSuccessRenderer,
} from 'components/multiStepModal/types';

export function MultiStepModal<TData = unknown, TVariables = unknown>({
  open,
  steps,
  currentStep,
  onStepChange,
  onRequestClose,
  onRequestSuccessClose,
  onRequestMinimize,
  success,
  submit,
  labels,
  className = '',
  wrapClassName,
  width = 845,
  centered = true,
  destroyOnClose = true,
  isMinimizing = false,
}: MultiStepModalProps<TData, TVariables>) {
  const { t } = useTranslation();
  const stepsCount = steps.length;
  const isSuccessOpen = Boolean(submit?.mutation.isSuccess);
  // The parent owns the current step. We only clamp defensively for rendering.
  const safeCurrentStep = clampStep(currentStep, stepsCount);
  const isFinalStep = safeCurrentStep === stepsCount - 1;
  const step = steps[safeCurrentStep];

  const requestStepChange = useCallback(
    (nextStep: number) => {
      const clampedStep = clampStep(nextStep, stepsCount);

      // Avoid redundant notifications when the requested step is already active.
      if (clampedStep === safeCurrentStep) {
        return;
      }

      onStepChange(clampedStep);
    },
    [onStepChange, safeCurrentStep, stepsCount],
  );

  const {
    disabled: primaryDisabled,
    loading: primaryLoading,
    errorMessage,
    handlePrimaryAction,
  } = useWizardPrimaryAction({
    currentStep: safeCurrentStep,
    isFinalStep,
    step,
    requestStepChange,
    submit,
    fallbackSubmitErrorMessage: t('global:internet_connexion_error'),
  });

  const modalClassName =
    `multi-step-modal ${isMinimizing ? 'is-minimizing' : ''} [&_.ant-modal-content]:p-0 [&_.ant-modal-body]:!p-0 [&_.ant-modal-content]:rounded-2xl [&_.ant-modal-content]:overflow-hidden [&_.ant-modal-content]:shadow-none md:[&_.ant-modal-body_.content]:w-full lg:[&_.ant-modal-body_.content]:px-16 ${className}`.trim();

  const previousLabel =
    safeCurrentStep === 0
      ? labels?.quit ?? t('global:quitter')
      : labels?.previous ?? t('global:previous');
  const primaryLabel = isFinalStep
    ? labels?.submit ?? t('global:soumettre_mon_dossier')
    : labels?.next ?? t('global:continuer');

  const handlePreviousAction = useCallback(() => {
    if (safeCurrentStep === 0) {
      onRequestClose();
      return;
    }

    const shouldResetMutation = isFinalStep && (submit?.resetOnBack ?? true);
    if (shouldResetMutation) {
      submit?.mutation.reset?.();
    }

    requestStepChange(safeCurrentStep - 1);
  }, [isFinalStep, onRequestClose, requestStepChange, safeCurrentStep, submit]);

  const chevronSteps = steps.map((wizardStep, index) => ({
    key: index,
    title: wizardStep.title,
  }));

  const closeSuccess = useCallback(() => {
    // Success content comes from the parent, but modal closing stays centralized
    // here so both flows share the same reset/close behavior.
    if (onRequestSuccessClose) {
      onRequestSuccessClose();
      return;
    }

    submit?.mutation.reset?.();
  }, [onRequestSuccessClose, submit]);

  return (
    <>
      <AntModal
        open={open && !isSuccessOpen}
        onCancel={onRequestClose}
        footer={null}
        width={width}
        className={modalClassName}
        wrapClassName={wrapClassName}
        destroyOnClose={destroyOnClose}
        centered={centered}
        closable={false}
        rootClassName="[&_.ant-modal]:!max-w-[845px]"
      >
        <div className="flex h-full flex-col items-center md:h-[85vh]">
          <div className="w-full flex items-center justify-between px-6 bg-white border-b-[1px] border-color-border">
            <div className="flex items-center bg-white">
              <ChevronSteps
                steps={chevronSteps}
                currentStep={safeCurrentStep}
              />
            </div>

            <div className="flex items-center gap-3">
              <Button
                type="link"
                size="small"
                icon={<ArrowsAltOutlined />}
                onClick={onRequestMinimize}
                aria-label={t('global:collapse')}
                title={t('global:collapse')}
                className="!w-6 !h-6 !p-0 flex items-center justify-center !text-gris-hellio hover:!text-gray-600 transition-colors"
              />
              <Button
                type="link"
                size="small"
                icon={<CloseOutlined />}
                onClick={onRequestClose}
                aria-label={t('global:close')}
                title={t('global:close')}
                className="!w-6 !h-6 !p-0 flex items-center justify-center !text-gris-hellio hover:!text-gray-600 transition-colors"
              />
            </div>
          </div>

          <div className="flex overflow-y-auto h-full w-full flex-col">
            <div className="content flex-1 overflow-x-hidden bg-white py-6">
              {step?.content}
            </div>

            <div className="w-full rounded-b-xl border-t-[1px] border-color-border bg-white px-6 py-4 sticky bottom-0">
              {errorMessage && (
                <Alert
                  type="error"
                  showIcon
                  className="mb-4"
                  message={errorMessage}
                />
              )}

              <Flex justify="space-between" align="center">
                <Button
                  onClick={handlePreviousAction}
                  shape="round"
                  className="border-bleu-hellio-500 text-bleu-hellio-500 hover:!border-bleu-hellio-600 hover:!text-bleu-hellio-600"
                  size="large"
                >
                  {previousLabel}
                </Button>

                <Button
                  loading={primaryLoading}
                  shape="round"
                  onClick={handlePrimaryAction}
                  disabled={!step || primaryDisabled}
                  type="primary"
                  size="large"
                >
                  {primaryLabel}
                </Button>
              </Flex>
            </div>
          </div>
        </div>
      </AntModal>

      <WhiteModal
        open={isSuccessOpen}
        onCancel={() => undefined}
        title=""
        footer={null}
        closable={false}
        centered={centered}
        maskClosable={false}
        keyboard={false}
        className="[&_.ant-modal-body]:!rounded-t-xl"
      >
        <div className="h-full p-6 md:h-[60vh] flex justify-center items-center">
          {success({ closeSuccess })}
        </div>
      </WhiteModal>
    </>
  );
}
